import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Shield, LogOut } from 'lucide-react';
import { clearAuth } from '../../utils/auth';
import { apiService } from '../../services/api';
import axios from 'axios';


const UserInfo = () => {
  const navigate = useNavigate();
  const [userContext, setUserContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [publicationsCount, setPublicationsCount] = useState(0);
  const [followsCount, setFollowsCount] = useState(0);
  const [accountInfo, setAccountInfo] = useState(null);
  const [reputationInfo, setReputationInfo] = useState(null);
  const [userStats, setUserStats] = useState({
    totalLikes: 0,
    totalFavorites: 0,
    myFavoritesCount: 0
  });
  const [chartsStats, setChartsStats] = useState({
    bazi: 0,
    liuyao: 0,
    qimen: 0,
    total: 0
  });

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        // First try to get from local storage for immediate display
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          setUserContext({
            user: JSON.parse(storedUser),
            subscriptions: JSON.parse(localStorage.getItem('subscriptions') || '[]'),
            permissions: JSON.parse(localStorage.getItem('permissions') || '[]')
          });
        }

        // Then fetch fresh data from API
        // We need an endpoint that returns full user context (user + subs + perms)
        // Assuming apiService.getMe() or similar exists and returns this structure
        // If not, we might need to create one or use existing login response structure
        
        // Since we don't have a dedicated "get full context" API other than login,
        // let's try to use the /api/me endpoint if it exists and returns full data,
        // or we might need to rely on what we have.
        
        // However, based on previous context, the login response returns:
        // { user, subscriptions, permissions, access_token }
        // We should probably add a 'getMe' method to apiService that hits an endpoint returning this.
        
        // Let's assume we can use a new method or existing one.
        // If apiService.getMe() returns just the user, we might need to update backend.
        // But let's try to fetch using a hypothetical getMyProfile() that we'll add to apiService if needed.
        
        // For now, let's try to re-login silently or just fetch if we can.
        // Actually, let's check if we can use the admin API for self if we are admin, 
        // but for regular users we need a way.
        
        // Let's try to fetch from a new endpoint or existing one.
        // If we look at backend/app.py, there might be a /api/me or similar.
        // If not, we should rely on what we have or ask to implement it.
        
        // Wait, the user said "没有查询到自己的记录" (No records found).
        // This implies the local storage might be empty of subscriptions even if they exist in DB.
        // We MUST fetch from server.
        
        const data = await apiService.getMe(); // We need to ensure this returns subscriptions
        
        if (data) {
            // Update local storage and state
            localStorage.setItem('user', JSON.stringify(data.user));
            localStorage.setItem('subscriptions', JSON.stringify(data.subscriptions || []));
            localStorage.setItem('permissions', JSON.stringify(data.permissions || []));
            
            setUserContext({
                user: data.user,
                subscriptions: data.subscriptions || [],
                permissions: data.permissions || []
            });
        } else if (!storedUser) {
            navigate('/login');
        }

      } catch (e) {
        console.error("Failed to load user info", e);
        if (!localStorage.getItem('user')) {
            navigate('/login');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [navigate]);

  // 获取发布数量统计和用户统计信息
  useEffect(() => {
    const fetchPublicationsCount = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const response = await axios.get('/api/public/my-publications', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.success) {
          const publications = response.data.data || [];
          
          // 计算总点赞数和总收藏数（被收藏的次数）
          const totalLikes = publications.reduce((sum, pub) => sum + (pub.likes_count || 0), 0);
          const totalFavorites = publications.reduce((sum, pub) => sum + (pub.favorites_count || 0), 0);
          
          setPublicationsCount(publications.length);
          setUserStats(prev => ({
            ...prev,
            totalLikes: totalLikes,
            totalFavorites: totalFavorites
          }));
        }
      } catch (error) {
        console.error('获取发布统计失败:', error);
        setPublicationsCount(0);
        setUserStats(prev => ({ ...prev, totalLikes: 0, totalFavorites: 0 }));
      }
    };
    
    if (userContext) {
      fetchPublicationsCount();
    }
  }, [userContext]);

  // 获取用户收藏数量统计
  useEffect(() => {
    const fetchMyFavoritesCount = async () => {
      try {
        const token = localStorage.getItem('access_token');
        // 使用一个简单的查询来获取用户收藏的数量
        // 这里我们可以通过查询所有类型的图表来获取用户收藏的数量
        const response = await axios.get('/api/public/my-favorites-count', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.success) {
          setUserStats(prev => ({
            ...prev,
            myFavoritesCount: response.data.count || 0
          }));
        }
      } catch (error) {
        console.error('获取收藏统计失败:', error);
        // 如果API不存在，我们可以设置为0或者使用其他方式
        setUserStats(prev => ({ ...prev, myFavoritesCount: 0 }));
      }
    };

    if (userContext) {
      fetchMyFavoritesCount();
    }
  }, [userContext]);

  // 获取关注数量统计
  useEffect(() => {
    const fetchFollowsCount = async () => {
      try {
        const result = await apiService.getMyFollows(1, 10); // 获取所有关注用户
        if (result.success) {
          setFollowsCount(result.data.data?.length || 0);
        }
      } catch (error) {
        console.error('获取关注统计失败:', error);
      }
    };

    if (userContext) {
      fetchFollowsCount();
    }
  }, [userContext]);

  // 获取卦例统计信息
  useEffect(() => {
    const fetchChartsStats = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const response = await axios.get('/api/public/user/charts-stats', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.success) {
          const stats = response.data.data;
          setChartsStats({
            bazi: stats.bazi || 0,
            liuyao: stats.liuyao || 0,
            qimen: stats.qimen || 0,
            total: (stats.bazi || 0) + (stats.liuyao || 0) + (stats.qimen || 0)
          });
        }
      } catch (error) {
        console.error('获取卦例统计失败:', error);
        setChartsStats({ bazi: 0, liuyao: 0, qimen: 0, total: 0 });
      }
    };

    if (userContext) {
      fetchChartsStats();
    }
  }, [userContext]);

  // 获取积分账户信息
  useEffect(() => {
    const fetchAccountInfo = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const response = await axios.get('/api/jifen/account', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAccountInfo(response.data);
      } catch (error) {
        console.error('获取积分账户信息失败:', error);
        // 设置默认值
        setAccountInfo({ balance: 0, status: 1 });
      }
    };

    if (userContext) {
      fetchAccountInfo();
    }
  }, [userContext]);

  // 获取声望信息
  useEffect(() => {
    const fetchReputationInfo = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const response = await axios.get('/api/jifen/reputation', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setReputationInfo(response.data);
      } catch (error) {
        console.error('获取声望信息失败:', error);
        // 设置默认值
        setReputationInfo({ current_score: 0, level_name: '未获取' });
      }
    };

    if (userContext) {
      fetchReputationInfo();
    }
  }, [userContext]);

  const handleLogout = () => {
    clearAuth();
    navigate('/');
    window.location.reload();
  };

  // 计算订阅统计
  const getSubscriptionStats = () => {
    const validCount = subscriptions.filter(s => s.is_valid).length;
    const totalAmount = subscriptions.reduce((sum, s) => sum + (s.actual_amount || 0), 0);
    return { validCount, totalAmount, total: subscriptions.length };
  };

  if (loading && !userContext) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!userContext) return null;

  const { user, subscriptions } = userContext;
  const subStats = getSubscriptionStats();

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="flex-1 pb-10">
        {/* 顶部背景 */}
        <div className="bg-gradient-to-br from-indigo-700 via-indigo-600 to-cyan-600 h-2 sm:h-3 relative">
          <div className="absolute bottom-0 left-0 w-full h-1 sm:h-2 bg-slate-50 rounded-t-3xl"></div>
        </div>

        <div className="max-w-5xl mx-auto px-3 sm:px-6 relative z-10">
          {/* 个人资料卡片 */}
          <div className="bg-white rounded-2xl shadow-md border border-slate-100 p-4 sm:p-6 mb-4 sm:mb-6">
            <div className="flex flex-col gap-4">
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-full bg-indigo-100 flex items-center justify-center border-4 border-white shadow-sm shrink-0">
                  <User className="h-8 w-8 sm:h-10 sm:w-10 text-indigo-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h1 className="text-xl sm:text-2xl font-bold text-slate-900 leading-tight truncate">{user.nickname || '未设置昵称'}</h1>
                  <p className="text-slate-500 text-xs sm:text-sm mt-1 break-all">{user.email}</p>
                  <div className="flex flex-wrap gap-1.5 sm:gap-2 mt-3">
                    {user.is_admin && (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        <Shield className="w-3 h-3 mr-1" />
                        管理员
                      </span>
                    )}
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                      💰 {accountInfo?.balance || 0} 积分
                    </span>
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-violet-100 text-violet-800">
                      ⭐ {reputationInfo?.current_score || 0} 声望
                    </span>
                  </div>
                </div>
              </div>

              <button
                onClick={handleLogout}
                className="w-full sm:w-auto sm:self-end flex items-center justify-center px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
              >
                <LogOut className="w-4 h-4 mr-2" />
                退出登录
              </button>
            </div>
          </div>

          {/* 功能板块 */}
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            {/* 我的订阅板块 */}
            <div
              onClick={() => navigate('/user/subscriptions')}
              className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 sm:p-5 cursor-pointer hover:shadow-md hover:border-indigo-200 transition-all group"
            >
              <h3 className="font-bold text-gray-900 mb-3">我的订阅</h3>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">总订阅数</span>
                  <span className="font-medium text-gray-900">{subStats.total}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">生效中</span>
                  <span className="font-medium text-green-600">{subStats.validCount}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">累计消费</span>
                  <span className="font-medium text-indigo-600">¥{subStats.totalAmount.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* 我的积分板块 */}
            <div
              onClick={() => navigate('/jifen')}
              className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 sm:p-5 cursor-pointer hover:shadow-md hover:border-indigo-200 transition-all group"
            >
              <h3 className="font-bold text-gray-900 mb-3">我的积分</h3>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">当前余额</span>
                  <span className="font-medium text-green-600">{accountInfo?.balance || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">积分充值</span>
                  <span className="font-medium text-indigo-600">→</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">消费记录</span>
                  <span className="font-medium text-blue-600">→</span>
                </div>
              </div>
            </div>

            {/* 我的声望板块 */}
            <div
              onClick={() => navigate('/jifen/reputation')}
              className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 sm:p-5 cursor-pointer hover:shadow-md hover:border-indigo-200 transition-all group"
            >
              <h3 className="font-bold text-gray-900 mb-3">我的声望</h3>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">当前等级</span>
                  <span className="font-medium text-purple-600">{reputationInfo?.level_name || '未获取'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">声望值</span>
                  <span className="font-medium text-pink-600">{reputationInfo?.current_score || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">等级详情</span>
                  <span className="font-medium text-indigo-600">→</span>
                </div>
              </div>
            </div>

            {/* 违规举报板块 -在 jifen 页面下*/}
            <div
              className="bg-slate-100 rounded-2xl shadow-sm border border-slate-200 p-4 sm:p-5 opacity-60"
            >
              <h3 className="font-bold text-gray-500 mb-3">违规举报</h3>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">举报违规</span>
                  <span className="font-medium text-gray-400">→</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">维护秩序</span>
                  <span className="font-medium text-gray-400">→</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">匿名举报</span>
                  <span className="font-medium text-gray-400">→</span>
                </div>
              </div>
            </div>

            {/* 我的卦例板块 */}
            <div
              onClick={() => navigate('/user/gualis')}
              className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 sm:p-5 cursor-pointer hover:shadow-md hover:border-indigo-200 transition-all group"
            >
              <h3 className="font-bold text-gray-900 mb-3">我的卦例</h3>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">八字</span>
                  <span className="font-medium text-red-600">{chartsStats.bazi}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">六爻</span>
                  <span className="font-medium text-blue-600">{chartsStats.liuyao}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">奇门</span>
                  <span className="font-medium text-green-600">{chartsStats.qimen}</span>
                </div>
              </div>
            </div>

            {/* 我的发布板块 */}
            <div
              onClick={() => navigate('/user/publications')}
              className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 sm:p-5 cursor-pointer hover:shadow-md hover:border-indigo-200 transition-all group"
            >
              <h3 className="font-bold text-gray-900 mb-3">我的发布</h3>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">已发布</span>
                  <span className="font-medium text-gray-900">{publicationsCount}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">求助中</span>
                  <span className="font-medium text-blue-600">—</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">分享中</span>
                  <span className="font-medium text-green-600">—</span>
                </div>
              </div>
            </div>

            {/* 我的收藏板块 */}
            <div
              onClick={() => navigate('/user/favorites')}
              className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 sm:p-5 cursor-pointer hover:shadow-md hover:border-indigo-200 transition-all group"
            >
              <h3 className="font-bold text-gray-900 mb-3">我的收藏</h3>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">收藏总数</span>
                  <span className="font-medium text-red-600">{userStats.myFavoritesCount}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">查看收藏</span>
                  <span className="font-medium text-pink-600">→</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">管理收藏</span>
                  <span className="font-medium text-indigo-600">→</span>
                </div>
              </div>
            </div>

            {/* 我的关注板块 */}
            <div
              onClick={() => navigate('/user/follows')}
              className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 sm:p-5 cursor-pointer hover:shadow-md hover:border-indigo-200 transition-all group"
            >
              <h3 className="font-bold text-gray-900 mb-3">我的关注</h3>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">关注用户</span>
                  <span className="font-medium text-gray-900">{followsCount}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">查看动态</span>
                  <span className="font-medium text-indigo-600">→</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">发现更多</span>
                  <span className="font-medium text-pink-600">→</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
  
    </div>
  );
};

export default UserInfo;
